import { pgTable, uuid, text, integer, real, boolean, timestamp, date, numeric, index, uniqueIndex, unique } from 'drizzle-orm/pg-core'
import { sql } from 'drizzle-orm'
import { tenants } from './tenants'
import { users } from './users'
import { projects } from './projects'
import { TRANSLATION_SOURCES, TRANSLATION_REVIEW_STATUSES } from './knowledge'

export const TICKET_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'NEEDS_INFO',
  'TRIAGED',
  'AWAITING_EXPERT',
  'EXPERT_REVIEWING',
  'RESPONSE_DRAFTING',
  'RESPONSE_READY',
  'RESPONDED',
  'AWAITING_REPLY',
  'CLOSED',
  'ARCHIVED',
] as const

export type TicketStatus = typeof TICKET_STATUSES[number]

// Defined before tickets to satisfy FK reference from ticket_processing_runs → tickets,
// but tickets.current_processing_run_id is a plain uuid (no FK) to break the circular dep.
export const tickets = pgTable(
  'tickets',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    submitterId: uuid('submitter_id').notNull().references(() => users.id),
    assignedExpertId: uuid('assigned_expert_id').references(() => users.id),
    referenceNumber: text('reference_number').notNull(),
    status: text('status').notNull().$type<TicketStatus>(),
    aiProcessingState: text('ai_processing_state', {
      enum: ['idle', 'queued', 'running', 'failed', 'degraded'],
    }).notNull().default('idle'),
    aiFailureReason: text('ai_failure_reason'),
    // Plain uuid — no FK; circular dep with ticket_processing_runs and ticket_messages
    currentProcessingRunId: uuid('current_processing_run_id'),
    lastMessageId: uuid('last_message_id'),
    clarificationCycleCount: integer('clarification_cycle_count').notNull().default(0),
    priority: text('priority', { enum: ['low', 'medium', 'high', 'critical'] }),
    categoryTags: text('category_tags').array().notNull().default(sql`'{}'`),
    sentimentScore: real('sentiment_score'),
    urgencyScore: real('urgency_score'),
    infoCompletenessScore: real('info_completeness_score'),
    title: text('title'),
    channel: text('channel', { enum: ['web', 'whatsapp', 'slack'] }).notNull().default('web'),
    version: integer('version').notNull().default(1),
    // Structured intake fields (R fields) — nullable for backward compat; required on new submissions
    projectId: uuid('project_id').references(() => projects.id),
    department: text('department'),
    dateOfOccurrence: date('date_of_occurrence'),
    issueCategory: text('issue_category'),
    affectedSystem: text('affected_system'),
    userPriority: text('user_priority', { enum: ['critical', 'high', 'medium', 'low'] }),
    successCriteria: text('success_criteria'),
    actionRequested: text('action_requested'),
    // Expert resolution fields (S fields)
    resolutionSeverity: text('resolution_severity'),
    resolutionConcerns: text('resolution_concerns'),
    resolvedAt: timestamp('resolved_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    uniqTenantRef: unique('tickets_tenant_reference_unique').on(t.tenantId, t.referenceNumber),
  }),
)

export const ticketMessages = pgTable(
  'ticket_messages',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    ticketId: uuid('ticket_id').notNull().references(() => tickets.id),
    // NULL for system messages
    authorId: uuid('author_id').references(() => users.id),
    role: text('role', { enum: ['line_manager', 'expert', 'system'] }).notNull(),
    language: text('language', { enum: ['en', 'es'] }).notNull().default('en'),
    bodyRaw: text('body_raw').notNull(),
    bodySanitized: text('body_sanitized').notNull(),
    idempotencyKey: text('idempotency_key'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    // Partial unique: only enforce when idempotency_key is set
    uniqIdempotency: uniqueIndex('ticket_messages_idempotency_unique')
      .on(t.ticketId, t.idempotencyKey)
      .where(sql`idempotency_key IS NOT NULL`),
  }),
)

export const ticketTranslations = pgTable(
  'ticket_translations',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    ticketId: uuid('ticket_id').notNull().references(() => tickets.id, { onDelete: 'cascade' }),
    language: text('language', { enum: ['en', 'es'] }).notNull(),
    title: text('title'),
    department: text('department'),
    issueCategory: text('issue_category'),
    affectedSystem: text('affected_system'),
    successCriteria: text('success_criteria'),
    actionRequested: text('action_requested'),
    sourceContentHash: text('source_content_hash').notNull(),
    translationSource: text('translation_source', { enum: TRANSLATION_SOURCES }).notNull(),
    reviewStatus: text('review_status', { enum: TRANSLATION_REVIEW_STATUSES }).notNull().default('pending'),
    reviewedById: uuid('reviewed_by_id').references(() => users.id),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    uniqTicketLanguage: unique('ticket_translations_ticket_language_unique').on(t.ticketId, t.language),
    tenantTicketIdx: index('ticket_translations_tenant_idx').on(t.tenantId, t.ticketId),
  }),
)

export const ticketProcessingRuns = pgTable('ticket_processing_runs', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
  ticketId: uuid('ticket_id').notNull().references(() => tickets.id),
  hookName: text('hook_name').notNull(),
  triggerEvent: text('trigger_event').notNull(),
  sourceTicketVersion: integer('source_ticket_version').notNull(),
  sourceMessageId: uuid('source_message_id').references(() => ticketMessages.id),
  status: text('status', {
    enum: ['queued', 'running', 'succeeded', 'failed', 'cancelled', 'stale'],
  }).notNull(),
  attemptCount: integer('attempt_count').notNull().default(0),
  maxAttempts: integer('max_attempts').notNull().default(3),
  idempotencyKey: text('idempotency_key').notNull(),
  startedAt: timestamp('started_at', { withTimezone: true }),
  expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
  completedAt: timestamp('completed_at', { withTimezone: true }),
  errorCode: text('error_code'),
  errorMessage: text('error_message'),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
})

export const aiCallLogs = pgTable(
  'ai_call_logs',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    ticketId: uuid('ticket_id').references(() => tickets.id),
    processingRunId: uuid('processing_run_id').references(() => ticketProcessingRuns.id),
    agentName: text('agent_name').notNull(),
    provider: text('provider').notNull(),
    model: text('model').notNull(),
    promptTokens: integer('prompt_tokens'),
    completionTokens: integer('completion_tokens'),
    totalTokens: integer('total_tokens'),
    estimatedCostUsd: numeric('estimated_cost_usd', { precision: 10, scale: 6 }),
    latencyMs: integer('latency_ms').notNull(),
    success: boolean('success').notNull(),
    errorCode: text('error_code'),
    errorMessage: text('error_message'),
    usedFallback: boolean('used_fallback').notNull().default(false),
    ownApiKey: boolean('own_api_key').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    tenantCreatedAtIdx: index('ai_call_logs_tenant_created_at_idx').on(t.tenantId, t.createdAt),
    ticketIdx: index('ai_call_logs_ticket_id_idx').on(t.ticketId),
    agentSuccessCreatedAtIdx: index('ai_call_logs_agent_success_created_at_idx').on(t.agentName, t.success, t.createdAt),
  }),
)

export const aiArtifacts = pgTable('ai_artifacts', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
  ticketId: uuid('ticket_id').notNull().references(() => tickets.id),
  processingRunId: uuid('processing_run_id').references(() => ticketProcessingRuns.id),
  sourceMessageId: uuid('source_message_id').references(() => ticketMessages.id),
  triggerEvent: text('trigger_event').notNull(),
  artifactType: text('artifact_type', {
    enum: [
      'completeness_assessment',
      'clarification_questions',
      'summary',
      'suggested_response',
      'kb_update_proposal',
      'tag_suggestion',
      'sentiment_analysis',
    ],
  }).notNull(),
  language: text('language', { enum: ['en', 'es'] }).notNull().default('en'),
  content: text('content').notNull(),
  contentJson: text('content_json'), // stored as JSON string; parse at application layer
  modelUsed: text('model_used'),
  promptVersion: text('prompt_version'),
  validationStatus: text('validation_status', {
    enum: ['valid', 'invalid', 'fallback_used'],
  }).notNull(),
  expertAccepted: boolean('expert_accepted'),
  expertEdited: boolean('expert_edited').notNull().default(false),
  expertVersion: text('expert_version'),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
})

export const ticketMessageTranslations = pgTable(
  'ticket_message_translations',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    messageId: uuid('message_id').notNull().references(() => ticketMessages.id, { onDelete: 'cascade' }),
    language: text('language', { enum: ['en', 'es'] }).notNull(),
    body: text('body').notNull(),
    sourceBodyHash: text('source_body_hash').notNull(),
    translationSource: text('translation_source', { enum: TRANSLATION_SOURCES }).notNull(),
    reviewStatus: text('review_status', { enum: TRANSLATION_REVIEW_STATUSES }).notNull().default('pending'),
    reviewedById: uuid('reviewed_by_id').references(() => users.id),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    uniqMessageLanguage: unique('ticket_message_translations_message_language_unique').on(t.messageId, t.language),
    tenantMessageIdx: index('ticket_message_translations_tenant_idx').on(t.tenantId, t.messageId),
  }),
)

export const aiArtifactTranslations = pgTable(
  'ai_artifact_translations',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    artifactId: uuid('artifact_id').notNull().references(() => aiArtifacts.id, { onDelete: 'cascade' }),
    language: text('language', { enum: ['en', 'es'] }).notNull(),
    content: text('content').notNull(),
    sourceContentHash: text('source_content_hash').notNull(),
    translationSource: text('translation_source', { enum: TRANSLATION_SOURCES }).notNull(),
    reviewStatus: text('review_status', { enum: TRANSLATION_REVIEW_STATUSES }).notNull().default('pending'),
    reviewedById: uuid('reviewed_by_id').references(() => users.id),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    uniqArtifactLanguage: unique('ai_artifact_translations_artifact_language_unique').on(t.artifactId, t.language),
    tenantArtifactIdx: index('ai_artifact_translations_tenant_idx').on(t.tenantId, t.artifactId),
  }),
)
